import {
  debounceTime,
  filter,
  map,
  takeUntil,
} from 'rxjs/operators';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { PeriodType } from '../../rma-classes/periodtype';
import { DashboardService } from '../../service/dashboard.service';
import { LocalstorageService } from '../../service/localstorage.service';
import { Period } from '../../models/entity/period';
import { WidgetSettings } from '../../models/dashboard/widget/widgetSettings';
import { DashboardSettings } from '../../models/dashboard/dashboardSettings';
import { RmaSelectOption } from '../../models/entity/rmaSelectOption.model';
import { RmaValidators } from '../../shared/rma-validators';
import { DASHBOARD_SETTINGS_CHANGED_DEBOUNCE_TIME } from './rma-dashboard.component';
import { RmaWidgetComponent } from './rma-widget.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';


import { RmaSelectComponent } from '../forms/rma-select/rma-select.component';
import {RmaPeriodComponent} from "../forms/rma-period/rma-period.component";
const DEFAULT_PERIOD_TYPE = PeriodType.UNDEFINED;
const DEFAULT_PRIMARY_PERIOD = new Period('2017-01-01', '2017-01-31');
const DEFAULT_SECONDARY_PERIOD = new Period('2017-02-01', '2017-02-28');

@Component({selector: 'rma-dashboard-settings-global',
  templateUrl: './rma-dashboard-settings-global.component.html',
  standalone: true,
  imports: [CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    RmaSelectComponent, RmaPeriodComponent,],
})
export class RmaDashboardSettingsGlobalComponent implements OnInit, OnDestroy {
  private localstorage = inject(LocalstorageService);
  private dashboardService = inject(DashboardService);
  private fb = inject(UntypedFormBuilder);

  public form: UntypedFormGroup;
  public widgetOptions$ = this.dashboardService.options$.pipe(
    map(options => options.widgetTypes.map(type => new RmaSelectOption('DASHBOARD.' + type.toUpperCase() + '.TITLE', type))));
  public buttonDisabled = true;
  public selectedWidgets: RmaWidgetComponent[] = [];

  public widgets$ = this.dashboardService.settings$.pipe(filter(settings => !!settings),
    map(({widgets}) => widgets),
  );

  public filteredOptions$ = combineLatest([this.widgetOptions$, this.widgets$]).pipe(
    // widgets names only for both
    map(([allWidgets, existingWidgets]) => [allWidgets.map(x=> x.value), existingWidgets.map(y => y.type)] ),
    // Filter out existing widgets from the list - BALANCE_ACCOUNT is an exception (can be added multiple times)
    map(([allWidgets, existingWidgets]) => allWidgets.filter(widgetName => widgetName === 'BALANCE_ACCOUNT' ?  true : !existingWidgets.includes(widgetName))),
    // to rma selection
    map((filteredWidgets) => filteredWidgets.map(widget => new RmaSelectOption('DASHBOARD.' + widget.toUpperCase() + '.TITLE', widget)) ),
  );

  private unusedAccountNumbers;
  private unsubscribe$ = new Subject<void>();

  constructor() {
    this.form = this.fb.group({
      settings: this.fb.group({
        periodType: [DEFAULT_PERIOD_TYPE],
        primaryPeriod: [DEFAULT_PRIMARY_PERIOD, [RmaValidators.customPeriodValid]],
        secondaryPeriod: [DEFAULT_SECONDARY_PERIOD, [RmaValidators.customPeriodValid]],
      }),
      widgetOption: [''],
    });

    this.dashboardService.selectedWidgets$.subscribe(widgets => {
      this.selectedWidgets = widgets;
    });

    this.dashboardService.settings$.pipe(filter(s => s != undefined),
      takeUntil(this.unsubscribe$))
      .subscribe(settings => {
        const formValue = this.form.get('settings').value;
        if (!this.dashboardService.settingsAreEqual(settings, formValue)) {
          this.updateForm(settings);
        }
      });

    this.form.get('settings').valueChanges.pipe(
      debounceTime(DASHBOARD_SETTINGS_CHANGED_DEBOUNCE_TIME),
      filter(value => !this.dashboardService.settingsAreEqual(value, this.dashboardService.settings$.getValue())),
      takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        this.buttonDisabled = !this.form.valid;
      });
  }

  public ngOnInit(): void {
    // prepare unusedAccountNumbers for adding BALANCE_ACCOUNT widgets
    combineLatest([this.dashboardService.options$, this.dashboardService.settings$]).pipe(
      map(([allWidgets, selectedWidgets]) =>
        // get BALANCE_ACCOUNT only
        [allWidgets.widgets.filter(widget => widget.type === 'BALANCE_ACCOUNT'),
          // accos from existing BALANCE_ACCOUNT widgets
          selectedWidgets.widgets.filter(widget => widget.type === 'BALANCE_ACCOUNT').map(widget => widget.specs.accno)]),
      map(([widgetsOptions, usedAccountNumbers]) => this.toUnusedAccnos(widgetsOptions, usedAccountNumbers)),
      takeUntil(this.unsubscribe$),
    ).subscribe(unusedAccNumbers => {
      this.unusedAccountNumbers = unusedAccNumbers
    })
  }

  get periodType(): string {
    return this.form.value.settings.periodType;
  }

  set periodType(val: string) {
    if (this.form.value.settings.periodType != val) {
      this.form.get('settings')
        .get('periodType')
        .setValue(val);
    }
  }

  public addWidget(type) {
    if (!type) return;
    const settings = this.dashboardService.settings$.getValue();
    const currentHighestId = settings.widgets[0] ? Math.max.apply(Math, settings.widgets.map(w => w.id)) : 0;

    if (type !== 'BALANCE_ACCOUNT') {
      this.createWidget(settings, currentHighestId, type, null);
    } else {
      if (!!this.unusedAccountNumbers?.length) {
        // Use first available account number as default option
        this.createWidget(settings, currentHighestId, type, {accno: this.unusedAccountNumbers[0]});
      }
    }
  }

  public updateForm(settings: DashboardSettings) {
    if (settings.primaryPeriod) {
      this.form.get('settings')
        .patchValue({
          periodType: settings.periodType || DEFAULT_PERIOD_TYPE,
          primaryPeriod: settings.primaryPeriod || DEFAULT_PRIMARY_PERIOD,
          secondaryPeriod: settings.secondaryPeriod || DEFAULT_SECONDARY_PERIOD,
        });
    }
  }

  public updateDashboardSettings() {
    const formSettingsValue = this.form.value.settings;
    const settings = this.dashboardService.settings$.getValue();

    if (formSettingsValue && settings) {
      settings.primaryPeriod = formSettingsValue.primaryPeriod;
      settings.secondaryPeriod = formSettingsValue.secondaryPeriod;
      settings.periodType = this.periodType;
    }

    this.buttonDisabled = true;
    this.dashboardService.settings$.next(settings);
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private createWidget(settings: DashboardSettings, currentHighestId: number, type: string, specs: {accno: string} | null) {
    const widget = new WidgetSettings(currentHighestId + 1, type, specs);
    settings.widgets = [...settings.widgets, widget];
    this.dashboardService.setSettings(settings);
    this.form.get('widgetOption')
      .patchValue(null);
  }

  private toUnusedAccnos(allBalancesWidgets, usedAccountNumbers) {
    const allCharts = allBalancesWidgets.map(widOpt => widOpt.charts);
    const flattenCharts = [].concat(...allCharts);
    const uniqueAccountNumbers = new Set(flattenCharts.map(chart => chart.accno));
    usedAccountNumbers.forEach(accNo => uniqueAccountNumbers.delete(accNo));
    return [...uniqueAccountNumbers];
  }
}
